
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  calculateLevelProgress, 
  getStreakMedal, 
  getBadges, 
  checkNewBadgeUnlocked 
} from '../utils/gamification';
import BadgeAlert from './BadgeAlert';
import ShareModal from './ShareModal';

// Import new component files
import GamificationAnimations from './gamification/GamificationAnimations';
import StatCards from './gamification/StatCards';
import LevelProgress from './gamification/LevelProgress';
import ActionButtons from './gamification/ActionButtons';
import GamificationStyles from './gamification/GamificationStyles';

interface GamificationProps {
  streak: number;
  points: number;
  completedDay: boolean;
  earnedPoints: number;
  currentDay?: number | null;  // Modified to accept null
  lessonsCompleted?: number | null;  // Modified to accept null
}

const Gamification: React.FC<GamificationProps> = ({ 
  streak, 
  points, 
  completedDay,
  earnedPoints,
  currentDay = 1,  // Default to 1 if not provided
  lessonsCompleted = 0  // Default to 0 if not provided
}) => {
  const [showAnimation, setShowAnimation] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);
  const [showMedalAnimation, setShowMedalAnimation] = useState(false);
  const [previousBadges, setPreviousBadges] = useState<any[]>([]);
  const [newBadge, setNewBadge] = useState<any>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const levelProgress = calculateLevelProgress(points);
  const medal = getStreakMedal(streak);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (completedDay) {
      // Save previous badges state to check for new badges
      const userProgress = { completedDays: [1], streak, points: points - earnedPoints };
      setPreviousBadges(getBadges(userProgress));
      
      // Show point animation immediately
      setShowAnimation(true);
      
      // Show medal animation if applicable (streaks of 7, 14, 30)
      if (streak === 7 || streak === 14 || streak === 30) {
        setShowMedalAnimation(true);
      }
      
      // Animate progress bar with slight delay
      setTimeout(() => setAnimateProgress(true), 500);
      
      // Check for new badges with slight delay
      setTimeout(() => {
        const currentUserProgress = { completedDays: [1], streak, points };
        const currentBadges = getBadges(currentUserProgress);
        const newlyUnlockedBadge = checkNewBadgeUnlocked(previousBadges, currentBadges);
        
        if (newlyUnlockedBadge) {
          setNewBadge(newlyUnlockedBadge);
        }
      }, 1500);
      
      // Hide animations after 3 seconds
      const timer = setTimeout(() => {
        setShowAnimation(false);
        setAnimateProgress(false);
        setShowMedalAnimation(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [completedDay, streak, points, earnedPoints]);
  
  const handleViewBadges = () => {
    navigate('/badges');
  };
  
  const handleDismissBadge = () => {
    setNewBadge(null);
  };
  
  const handleShareProgress = () => {
    setShareModalOpen(true);
  };
  
  return (
    <div className="relative">
      {newBadge && (
        <BadgeAlert badge={newBadge} onClose={handleDismissBadge} />
      )}
      
      {/* Animations component */}
      <GamificationAnimations
        showAnimation={showAnimation}
        showMedalAnimation={showMedalAnimation}
        earnedPoints={earnedPoints}
        medal={medal}
        streak={streak}
      />
      
      <div className={`bg-white p-5 rounded-2xl border border-hormony-beige/50 shadow-sm ${showAnimation ? 'opacity-30' : 'animate-slide-up'}`}>
        {/* Stat cards component */}
        <StatCards 
          streak={streak}
          points={points}
          medal={medal}
          currentDay={currentDay}
          lessonsCompleted={lessonsCompleted}
        />
        
        {/* Level progress component */}
        <LevelProgress 
          levelProgress={levelProgress}
          animateProgress={animateProgress}
        />
        
        {/* Action buttons component */}
        <ActionButtons 
          onViewBadges={handleViewBadges}
          onShareProgress={handleShareProgress}
        />
      </div>
      
      <ShareModal 
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        content={{
          type: 'progress',
          title: 'My Hormone Harmony Journey',
          description: medal.name ? `I've earned the ${medal.name} streak medal!` : 'Check out my progress on my hormone health journey!',
          points: points,
          streak: streak
        }}
      />
      
      {/* CSS Styles */}
      <GamificationStyles />
    </div>
  );
};

export default Gamification;
